// src/features/mensagens/api/whatsappAdapter.js
// ============================================================================
// 🧩 WHATSAPP ADAPTER — Abre WhatsApp Real + Guarda en Firestore + Attachments
// 📦 V2 ENTERPRISE — wa.me link hoy · API oficial mañana · Attachments soportados
// ============================================================================
//
// REGLAS DE ORO:
// 1. HOY: Abre https://wa.me con mensaje + links de attachments
// 2. HOY: Guarda copia del mensaje + attachments en Firestore con status 'sent'
// 3. MAÑANA: Activar VITE_WHATSAPP_ENABLED=true para usar Graph API
// 4. Este archivo es el ÚNICO que cambia
// 5. V2: Soporte completo de attachments (imagen/documento) → Cloudinary URL
// ============================================================================

import { getMensagensService } from './mensagensService';
import { validateTelefone, validateMensagem } from '../utils/validators';
import { formatPhoneForWhatsApp } from '../utils/formatters';
import { MESSAGE_STATUS } from '../utils/constants';

// ============================================================================
// 🔧 CONFIGURACIÓN
// ============================================================================

const WHATSAPP_CONFIG = {
  // HOY: false → abre wa.me link
  // MAÑANA: true → usa Graph API de Meta
  enabled: import.meta.env.VITE_WHATSAPP_ENABLED === 'true' || false,
  apiVersion: 'v18.0',
  phoneNumberId: import.meta.env.VITE_WHATSAPP_PHONE_ID || '',
  accessToken: import.meta.env.VITE_WHATSAPP_TOKEN || '',
  baseURL: 'https://graph.facebook.com',
  limits: {
    textMaxLength: 4096,
    mediaMaxSize: { image: 5*1024*1024, video: 16*1024*1024, audio: 16*1024*1024, document: 100*1024*1024 },
    dailyLimit: 1000,
  },
};

// ============================================================================
// 📤 MÉTODO PRINCIPAL — ABRE WHATSAPP Y GUARDA EN FIRESTORE (V2: +Attachments)
// ============================================================================

/**
 * Envía mensaje a WhatsApp (con o sin attachments)
 * 
 * @param {Object} params
 * @param {string} params.to - Número de teléfono del destinatario
 * @param {string} params.message - Texto del mensaje (puede ser solo texto o acompañar attachments)
 * @param {Array} [params.attachments] - Array de attachments [{ type, url, name, size, mimeType, thumbnail }]
 * @param {string} params.clientId - ID del cliente en el sistema
 * @param {string} params.clientName - Nombre del cliente
 * @returns {Promise<Object>} Resultado del envío
 */
export async function sendWhatsAppMessage({ to, message, attachments, clientId, clientName }) {
  // 1. Validar teléfono
  const phoneValidation = validateTelefone(to);
  if (!phoneValidation.valid) {
    throw new Error(`Teléfono inválido: ${phoneValidation.error}`);
  }

  // 2. Validar mensaje (puede estar vacío si solo se envían attachments)
  const mensajeParaValidar = message || (attachments?.length ? '📎 Arquivo enviado' : '');
  const msgValidation = validateMensagem(mensajeParaValidar);
  if (!msgValidation.valid) {
    throw new Error(`Mensaje inválido: ${msgValidation.error}`);
  }

  const sanitizedMessage = msgValidation.sanitized;
  const normalizedPhone = formatPhoneForWhatsApp(phoneValidation.normalized || to);

  // 3. 🔥 V2: Construir mensaje completo con links de attachments
  let fullMessage = sanitizedMessage;
  const normalizedAttachments = [];
  
  if (attachments && attachments.length > 0) {
    // Normalizar attachments para guardar en Firestore
    for (const att of attachments) {
      normalizedAttachments.push({
        type: att.type || 'document',
        url: att.url || '',
        name: att.name || 'arquivo',
        size: att.size || 0,
        mimeType: att.mimeType || 'application/octet-stream',
        thumbnail: att.thumbnail || null,
        preview: att.preview || null,
        cloudinaryPublicId: att.cloudinaryPublicId || null,
        uploadedAt: new Date().toISOString()
      });
    }

    // Agregar links de attachments al mensaje de WhatsApp
    const attachmentLinks = normalizedAttachments
      .map(a => {
        const icon = a.type === 'image' ? '🖼️' : '📄';
        return `${icon} ${a.name}: ${a.url}`;
      })
      .join('\n');
    
    fullMessage = sanitizedMessage 
      ? `${sanitizedMessage}\n\n${attachmentLinks}`
      : attachmentLinks;
  }

  // 4. Guardar en Firestore (registro de la conversación)
  const service = getMensagensService();
  try {
    await service.sendMessage({
      clientId: clientId || to,
      clientName: clientName || 'Cliente',
      clientPhone: normalizedPhone,
      message: fullMessage,
      // 🔥 V2: Guardar attachments en Firestore
      attachments: normalizedAttachments.length > 0 ? normalizedAttachments : undefined,
      status: MESSAGE_STATUS.SENT,
    });
  } catch (err) {
    console.warn('⚠️ [WhatsApp] Error guardando en Firestore:', err.message);
    // No bloqueamos el envío si falla el guardado
  }

  // 5. Abrir WhatsApp (comportamiento real, igual que el sistema viejo)
  const waUrl = `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(fullMessage)}`;
  
  try {
    window.open(waUrl, '_blank', 'noopener,noreferrer');
  } catch (err) {
    console.warn('⚠️ [WhatsApp] Error abriendo WhatsApp:', err.message);
    // Fallback: redirigir en la misma ventana
    window.location.href = waUrl;
  }

  // 6. 🔥 V2: Si API de WhatsApp habilitada, intentar enviar media por Graph API
  if (WHATSAPP_CONFIG.enabled && normalizedAttachments.length > 0) {
    try {
      await sendWhatsAppMedia(normalizedPhone, normalizedAttachments, sanitizedMessage);
      console.log('✅ [WhatsApp] Media enviada por Graph API');
    } catch (err) {
      console.warn('⚠️ [WhatsApp] Error enviando media por API:', err.message);
      // No bloquea - ya se envió el texto con links por wa.me
    }
  }

  return {
    success: true,
    waUrl,
    simulated: !WHATSAPP_CONFIG.enabled,
    hasAttachments: normalizedAttachments.length > 0,
    attachmentsCount: normalizedAttachments.length,
  };
}

// ============================================================================
// 🔥 V2: ENVIAR MEDIA POR GRAPH API (Cuando VITE_WHATSAPP_ENABLED=true)
// ============================================================================

/**
 * Envía archivos multimedia a través de la Graph API de WhatsApp Business
 * @param {string} to - Número de teléfono del destinatario
 * @param {Array} attachments - Array de attachments normalizados
 * @param {string} caption - Texto que acompaña al archivo
 * @returns {Promise<void>}
 */
async function sendWhatsAppMedia(to, attachments, caption = '') {
  for (const att of attachments) {
    const mediaType = att.type === 'image' ? 'image' : 'document';
    
    // 1. Subir media a servidores de WhatsApp
    const mediaEndpoint = `${WHATSAPP_CONFIG.baseURL}/${WHATSAPP_CONFIG.apiVersion}/${WHATSAPP_CONFIG.phoneNumberId}/media`;
    
    const mediaResponse = await fetch(mediaEndpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${WHATSAPP_CONFIG.accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        messaging_product: 'whatsapp',
        type: mediaType,
        link: att.url // URL pública de Cloudinary
      })
    });

    if (!mediaResponse.ok) {
      const errorData = await mediaResponse.json().catch(() => ({}));
      throw new Error(`Error subiendo media a WhatsApp: ${mediaResponse.status} - ${JSON.stringify(errorData)}`);
    }

    const mediaData = await mediaResponse.json();
    
    if (!mediaData.id) {
      throw new Error('No se obtuvo ID de media de WhatsApp');
    }

    // 2. Enviar mensaje con el media adjunto
    const messageEndpoint = `${WHATSAPP_CONFIG.baseURL}/${WHATSAPP_CONFIG.apiVersion}/${WHATSAPP_CONFIG.phoneNumberId}/messages`;
    
    const messageBody = {
      messaging_product: 'whatsapp',
      to: to,
      type: mediaType,
      [mediaType]: {
        id: mediaData.id,
      }
    };

    // Agregar caption si hay texto (solo para imágenes)
    if (caption && mediaType === 'image') {
      messageBody[mediaType].caption = caption.substring(0, WHATSAPP_CONFIG.limits.textMaxLength);
    }
    
    // Para documentos, agregar filename
    if (mediaType === 'document') {
      messageBody[mediaType].filename = att.name || 'documento';
      if (caption) {
        messageBody[mediaType].caption = caption.substring(0, WHATSAPP_CONFIG.limits.textMaxLength);
      }
    }

    const messageResponse = await fetch(messageEndpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${WHATSAPP_CONFIG.accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(messageBody)
    });

    if (!messageResponse.ok) {
      const errorData = await messageResponse.json().catch(() => ({}));
      throw new Error(`Error enviando mensaje WhatsApp: ${messageResponse.status} - ${JSON.stringify(errorData)}`);
    }

    console.log(`✅ [WhatsApp] Media enviada: ${att.name} (${mediaType})`);
  }
}

// ============================================================================
// 🔄 WEBHOOK HANDLER (Para cuando actives la API real)
// ============================================================================

/**
 * Maneja webhooks entrantes de WhatsApp (mensajes recibidos)
 * @param {Object} payload - Payload del webhook de Meta
 * @returns {Promise<Object|null>} Mensaje procesado o null
 */
export async function handleWhatsAppWebhook(payload) {
  if (!WHATSAPP_CONFIG.enabled) {
    console.warn('⚠️ [WhatsApp] Webhook recibido pero API no habilitada');
    return null;
  }

  try {
    const entry = payload?.entry?.[0];
    const changes = entry?.changes?.[0];
    const value = changes?.value;
    const messages = value?.messages;

    if (!messages?.length) return null;

    const message = messages[0];
    const contact = value?.contacts?.[0];

    // 🔥 V2: Detectar si el mensaje entrante tiene media
    let incomingAttachments = [];
    if (message.image) {
      incomingAttachments.push({
        type: 'image',
        url: message.image?.link || '',
        name: 'Imagem recebida',
        mimeType: message.image?.mime_type || 'image/jpeg',
        size: 0,
        whatsappMediaId: message.image?.id,
      });
    }
    if (message.document) {
      incomingAttachments.push({
        type: 'document',
        url: message.document?.link || '',
        name: message.document?.filename || 'Documento recebido',
        mimeType: message.document?.mime_type || 'application/octet-stream',
        size: 0,
        whatsappMediaId: message.document?.id,
      });
    }

    const service = getMensagensService();
    return await service.receiveMessage({
      clientId: contact?.wa_id,
      clientName: contact?.profile?.name || 'WhatsApp Contact',
      clientPhone: contact?.wa_id,
      message: message?.text?.body || message?.button?.text || '[Mensagem de mídia]',
      attachments: incomingAttachments.length > 0 ? incomingAttachments : undefined,
      whatsappMessageId: message?.id,
      timestamp: message?.timestamp ? new Date(parseInt(message.timestamp) * 1000).toISOString() : new Date().toISOString(),
    });
  } catch (error) {
    console.error('❌ [WhatsApp Webhook] Error:', error);
    return null;
  }
}

/**
 * Verifica la validez del webhook de WhatsApp
 * @param {string} mode - Modo de verificación
 * @param {string} token - Token de verificación
 * @param {string} challenge - Challenge de Meta
 * @returns {Object|null} Objeto con challenge si es válido
 */
export function verifyWebhook(mode, token, challenge) {
  const VERIFY_TOKEN = import.meta.env.VITE_WHATSAPP_VERIFY_TOKEN;
  if (mode === 'subscribe' && token === VERIFY_TOKEN) {
    console.log('✅ [WhatsApp Webhook] Verificado');
    return { challenge };
  }
  return null;
}

// ============================================================================
// 📊 ESTADO
// ============================================================================

/**
 * Verifica si la API de WhatsApp está completamente configurada
 * @returns {boolean}
 */
export function isWhatsAppEnabled() {
  return WHATSAPP_CONFIG.enabled && !!WHATSAPP_CONFIG.phoneNumberId && !!WHATSAPP_CONFIG.accessToken;
}

/**
 * Obtiene la configuración actual de WhatsApp (sin exponer secrets)
 * @returns {Object}
 */
export function getWhatsAppConfig() {
  return {
    enabled: WHATSAPP_CONFIG.enabled,
    mode: WHATSAPP_CONFIG.enabled ? 'api' : 'wa_link',
    limits: WHATSAPP_CONFIG.limits,
    phoneNumberId: WHATSAPP_CONFIG.phoneNumberId ? '***configurado***' : null,
  };
}

export default {
  send: sendWhatsAppMessage,
  webhook: { handle: handleWhatsAppWebhook, verify: verifyWebhook },
  status: { isEnabled: isWhatsAppEnabled, getConfig: getWhatsAppConfig },
};